import React, { useEffect, useMemo, useState } from "react";
import { Alert, Badge, Button, Form, Modal, Spinner, Table } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTrash, faUserPlus, faUsers } from "@fortawesome/free-solid-svg-icons";
import api from "../api/API";
import { useWorkspace } from "../context/WorkspaceContext";

const parseCollection = (response) => {
  if (Array.isArray(response?.data)) return response.data;
  if (response?.data?.status === false) {
    throw new Error(response?.data?.message || "Nao foi possivel carregar os membros.");
  }
  return response?.data?.records || response?.data?.data || [];
};

const getMemberId = (member) => String(member?.id_usuario ?? member?.user_id ?? member?.id ?? "");

const getMemberName = (member) =>
  member?.descricao ||
  member?.nome ||
  member?.usuario ||
  member?.email ||
  "Membro sem nome";

const getRoleValue = (member) =>
  String(member?.papel ?? member?.role ?? member?.role_label ?? "member").toLowerCase();

const normalizeRoleValue = (value) => {
  if (value === "0" || value === "membro" || value === "member") return "member";
  if (value === "1" || value === "admin" || value === "administrador") return "admin";
  if (value === "2" || value === "owner") return "owner";
  return value || "member";
};

const roleOptions = [
  { value: "member", label: "Membro" },
  { value: "admin", label: "Admin" },
  { value: "owner", label: "Owner" },
];

const roleLabelMap = {
  member: "Membro",
  admin: "Admin",
  owner: "Owner",
};

const MembersManager = ({ workspaceId: explicitWorkspaceId }) => {
  const { selectedWorkspaceId, selectedWorkspace } = useWorkspace();
  const workspaceId = String(explicitWorkspaceId || selectedWorkspaceId || "");

  const [members, setMembers] = useState([]);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  const [role, setRole] = useState("member");
  const [loading, setLoading] = useState(false);
  const [loadingMembers, setLoadingMembers] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [acessoProvisorio, setAcessoProvisorio] = useState(null);
  const [updatingMemberId, setUpdatingMemberId] = useState("");
  const [removingMemberId, setRemovingMemberId] = useState("");

  const fetchMembers = async () => {
    if (!workspaceId) {
      setMembers([]);
      return;
    }

    try {
      setLoadingMembers(true);
      setErrorMessage("");
      const response = await api.get(`/workspaces/${workspaceId}/members`);
      setMembers(parseCollection(response));
    } catch (error) {
      console.error("Erro ao buscar membros do workspace:", error);
      setErrorMessage(
        error?.response?.data?.message ||
          error?.message ||
          "Nao foi possivel carregar os membros do workspace."
      );
    } finally {
      setLoadingMembers(false);
    }
  };

  useEffect(() => {
    fetchMembers();
  }, [workspaceId]);

  const sortedMembers = useMemo(() => {
    return [...members].sort((left, right) => {
      const leftRole = getRoleValue(left);
      const rightRole = getRoleValue(right);
      const order = { owner: 0, admin: 1, member: 2 };
      const roleDiff = (order[leftRole] ?? 9) - (order[rightRole] ?? 9);
      if (roleDiff !== 0) return roleDiff;
      return getMemberName(left).localeCompare(getMemberName(right), "pt-BR");
    });
  }, [members]);

  const handleInvite = async () => {
    if (!workspaceId || !inviteEmail.trim()) return;

    try {
      setLoading(true);
      setErrorMessage("");
      setSuccessMessage("");
      setAcessoProvisorio(null);

      const payload = {
        email: inviteEmail.trim(),
        papel: normalizeRoleValue(role),
        role: normalizeRoleValue(role),
      };

      const response = await api.post(`/workspaces/${workspaceId}/members`, payload);

      if (response?.data?.status === false) {
        throw new Error(response?.data?.message || "Nao foi possivel enviar o convite.");
      }

      const dados = response?.data || {};

      setInviteEmail("");
      setRole("member");
      setShowInviteModal(false);
      setSuccessMessage(dados.message || "Convite enviado para o workspace.");

      // Conta criada sem o e-mail sair: a senha so existe nesta resposta.
      if (dados.usuarioCriado && !dados.conviteEnviado && dados.senhaTemporaria) {
        setAcessoProvisorio({ email: dados.email, senha: dados.senhaTemporaria });
      }
      fetchMembers();
    } catch (error) {
      console.error("Erro ao convidar membro do workspace:", error);
      setErrorMessage(
        error?.response?.data?.message ||
          error?.message ||
          "Nao foi possivel enviar o convite para o workspace."
      );
    } finally {
      setLoading(false);
    }
  };

  const handleRemove = async (memberId) => {
    if (!workspaceId || !memberId) return;
    if (!window.confirm("Tem certeza que deseja remover este membro do workspace?")) return;

    try {
      setRemovingMemberId(String(memberId));
      setErrorMessage("");
      setSuccessMessage("");
      await api.delete(`/workspaces/${workspaceId}/members/${memberId}`);
      setSuccessMessage("Membro removido do workspace.");
      fetchMembers();
    } catch (error) {
      console.error("Erro ao remover membro do workspace:", error);
      setErrorMessage(
        error?.response?.data?.message ||
          error?.message ||
          "Nao foi possivel remover o membro."
      );
    } finally {
      setRemovingMemberId("");
    }
  };

  const handleChangeRole = async (memberId, newRole) => {
    if (!workspaceId || !memberId) return;

    try {
      setUpdatingMemberId(String(memberId));
      setErrorMessage("");
      setSuccessMessage("");
      await api.patch(`/workspaces/${workspaceId}/members/${memberId}`, {
        papel: normalizeRoleValue(newRole),
        role: normalizeRoleValue(newRole),
      });
      setSuccessMessage("Permissao atualizada.");
      fetchMembers();
    } catch (error) {
      console.error("Erro ao alterar permissao do workspace:", error);
      setErrorMessage(
        error?.response?.data?.message ||
          error?.message ||
          "Nao foi possivel alterar a permissao."
      );
    } finally {
      setUpdatingMemberId("");
    }
  };

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-3 gap-3 flex-wrap">
        <div>
          <h5 className="mb-1">
            <FontAwesomeIcon icon={faUsers} /> Time do Workspace
          </h5>
          <small className="text-muted">
            {selectedWorkspace?.nome || "Workspace"} com {members.length} pessoa(s) vinculada(s).
          </small>
        </div>

        <Button variant="primary" onClick={() => setShowInviteModal(true)} disabled={!workspaceId}>
          <FontAwesomeIcon icon={faUserPlus} /> Convidar
        </Button>
      </div>

      {errorMessage && <Alert variant="danger">{errorMessage}</Alert>}
      {successMessage && <Alert variant="success">{successMessage}</Alert>}
      {acessoProvisorio && (
        <Alert variant="warning" onClose={() => setAcessoProvisorio(null)} dismissible>
          <div className="fw-bold mb-1">Repasse estes dados de acesso</div>
          <div>E-mail: <code>{acessoProvisorio.email}</code></div>
          <div>Senha temporaria: <code>{acessoProvisorio.senha}</code></div>
          <div className="mt-2 small">A senha nao vai aparecer de novo depois que voce fechar este aviso.</div>
        </Alert>
      )}

      {!workspaceId ? (
        <Alert variant="info" className="mb-0">
          Selecione um workspace para gerir convites e permissoes.
        </Alert>
      ) : loadingMembers ? (
        <div className="text-center py-4">
          <Spinner animation="border" size="sm" />
        </div>
      ) : sortedMembers.length === 0 ? (
        <Alert variant="light" className="mb-0">
          Ainda nao ha membros cadastrados neste workspace.
        </Alert>
      ) : (
        <Table striped bordered hover size="sm" responsive>
          <thead>
            <tr>
              <th>Nome</th>
              <th>Email</th>
              <th>Papel</th>
              <th>Acoes</th>
            </tr>
          </thead>
          <tbody>
            {sortedMembers.map((member) => {
              const memberId = getMemberId(member);
              const currentRole = getRoleValue(member);
              const isOwner = currentRole === "owner";

              return (
                <tr key={memberId || getMemberName(member)}>
                  <td>{getMemberName(member)}</td>
                  <td>{member.email || "-"}</td>
                  <td>
                    <Badge bg={isOwner ? "dark" : currentRole === "admin" ? "primary" : "secondary"}>
                      {roleLabelMap[currentRole] || currentRole}
                    </Badge>
                  </td>
                  <td>
                    <div className="d-flex gap-2 flex-wrap">
                      <Form.Select
                        size="sm"
                        style={{ maxWidth: 150 }}
                        value={currentRole}
                        disabled={isOwner || updatingMemberId === memberId}
                        onChange={(event) => handleChangeRole(memberId, event.target.value)}
                      >
                        {roleOptions.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </Form.Select>

                      <Button
                        variant="outline-danger"
                        size="sm"
                        disabled={isOwner || removingMemberId === memberId}
                        onClick={() => handleRemove(memberId)}
                      >
                        <FontAwesomeIcon icon={faTrash} />{" "}
                        {removingMemberId === memberId ? "Removendo..." : "Remover"}
                      </Button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </Table>
      )}

      <Modal show={showInviteModal} onHide={() => setShowInviteModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Convidar para o workspace</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Email do membro</Form.Label>
              <Form.Control
                type="email"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                placeholder="pessoa@empresa.com"
              />
            </Form.Group>

            <Form.Group>
              <Form.Label>Papel inicial</Form.Label>
              <Form.Select value={role} onChange={(e) => setRole(e.target.value)}>
                {roleOptions
                  .filter((option) => option.value !== "owner")
                  .map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
              </Form.Select>
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowInviteModal(false)}>
            Cancelar
          </Button>
          <Button variant="primary" onClick={handleInvite} disabled={loading || !inviteEmail.trim()}>
            {loading ? "Enviando..." : "Enviar convite"}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default MembersManager;
